class ProductGallery extends HTMLElement {
  static nativeTouchSpin = 'ontouchstart' in window || (window.navigator?.maxTouchPoints || 0) > 0;

  constructor() {
    super();

    this._selectors = {
      thumbnailsContainer: '[js-gallery-thumbnails]',
      thumbnail: '[js-gallery-thumbnail-slide]',
      mainCarousel: '[js-gallery-main]',
      mainCarouselWrapper: '[js-gallery-main-wrapper]',
      targetSlideBtn: '[js-navigate-slide]',
      nextSlideBtn: '[js-gallery-next-btn]',
      prevSlideBtn: '[js-gallery-prev-btn]',
      slideZoom: '[js-carousel-zoom]',
      zoomModal: '[js-carousel-zoom-modal]',
      magic360Shortcut: '[js-magic360-shortcut]',
    }
    this._selectors.inlinePlayButton = '[js-inline-play-button]';
    this._selectors.videoPoster = '[js-video-poster]';
    this._selectors.videoWrapper = '[js-video-wrapper]';
    this._selectors.videoSlide = '.swiper-slide--video';

    this._selectors.videoPlaceholder = '.video-placeholder';
    this.movedVideoPlayers = []; // To store details of all moved videos

    this.thumbnails = this.querySelectorAll(this._selectors.thumbnail)
    this.mainCarousel = this.querySelector(this._selectors.mainCarousel)
    this.mainCarouselWrapper = this.querySelector(this._selectors.mainCarouselWrapper)

    this.nextSlideBtn = this.querySelector(this._selectors.nextSlideBtn)
    this.prevSlideBtn = this.querySelector(this._selectors.prevSlideBtn)
    this.targetSlideBtn = this.querySelectorAll(this._selectors.targetSlideBtn)
    this.thumbnailsContainer = this.querySelector(this._selectors.thumbnailsContainer)
    this.currentSlide = 0
    this.magic360Shortcut = this.querySelector(this._selectors.magic360Shortcut + ' button')

    this.slideZooms = this.querySelectorAll(this._selectors.slideZoom)
    this.zoomModal = document.querySelector(this._selectors.zoomModal)

    this.zoomImages = this.zoomModal.querySelectorAll('.modal-gallery__content__image')

    // Custom Carousel Logic due to buggy vertical sliders
    this.isDragging = false;
    this.hasMoved = false;
    this.startPos = 0;
    this.scrollStartPos = 0;
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);

    // Logic to distinguish between mobile vs desktop thumbnail layout
    // Determines which direction to scroll the thumbnails in
    const isWideScreen = window.innerWidth > 1279;

    this.thumbnailDirection = {
      offset: isWideScreen ? "offsetTop" : "offsetLeft",
      scrollAxis: isWideScreen ? "scrollTop" : "scrollLeft",
      scrollDimensions: isWideScreen ? "scrollHeight" : "scrollWidth",
      page: isWideScreen ? "pageY" : "pageX",
      direction: isWideScreen ? "top" : "left",
    }

    window.addEventListener("resize", () => {
      const isWideScreen = window.innerWidth > 1279;

      this.thumbnailDirection = {
        offset: isWideScreen ? "offsetTop" : "offsetLeft",
        scrollAxis: isWideScreen ? "scrollTop" : "scrollLeft",
        scrollDimensions: isWideScreen ? "scrollHeight" : "scrollWidth",
        page: isWideScreen ? "pageY" : "pageX",
        direction: isWideScreen ? "top" : "left",
      }
    });
  }

  prepareVideoForModal(slide) {
      const videoWrapper = slide.querySelector(this._selectors.videoWrapper);
      
      // --- FIX: Corrected logic and removed stray semicolon ---
      if (!videoWrapper) return;
  
      // Check if the video is currently hidden (i.e., showing the poster image)
      if (videoWrapper.hidden) {
          // If the video player isn't active, we just need to know where it will live.
          // We find the player inside the hidden wrapper to prepare it for the move.
          this.videoOriginalParent = videoWrapper;
          this.activeVideoPlayer = videoWrapper.querySelector('iframe, video');
      } else {
          // If the video is already playing inline, the wrapper is visible.
          this.videoOriginalParent = videoWrapper;
          this.activeVideoPlayer = videoWrapper.firstElementChild; // The playing video/iframe
      }
  }
  
  handleModalToggle(mutations) {
    mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
            const modal = mutation.target;
            if (modal.hasAttribute('open')) {
                // Modal is opening: Move all videos into their modal placeholders.
                this.moveAllVideosToModal();
            } else {
                // Modal is closing: Return all videos to the carousel.
                this.returnAllVideosFromModal();
            }
        }
    });
}

moveAllVideosToModal() {
    const placeholders = this.zoomModal.querySelectorAll(this._selectors.videoPlaceholder);
    placeholders.forEach(placeholder => {
        const mediaId = placeholder.dataset.mediaId;
        const carouselVideoSlide = this.querySelector(`.swiper-slide--video[data-media-id="${mediaId}"]`);
        if (!carouselVideoSlide) return;

        const videoWrapper = carouselVideoSlide.querySelector(this._selectors.videoWrapper);
        const player = videoWrapper.querySelector('iframe, video');

        if (player) {
            // Check if the video was playing inline *before* we move it.
            const wasPlayingInline = !videoWrapper.hidden;

            this.movedVideoPlayers.push({
                player: player,
                originalParent: videoWrapper
            });
            
            placeholder.appendChild(player);

            // After moving the player, if it was playing, ensure it autoplays in the modal.
            // Since moving an iframe reloads it, we add the autoplay=1 parameter to its URL.
            if (wasPlayingInline && player.tagName === 'IFRAME') {
                if (!player.src.includes('autoplay=1')) {
                    player.src += (player.src.includes('?') ? '&' : '?') + 'autoplay=1';
                }
            } else if (wasPlayingInline && player.tagName === 'VIDEO') {
                player.play();
            }
        }
    });
}

returnAllVideosFromModal() {
    this.movedVideoPlayers.forEach(item => {
        // Move the player back to its original parent in the carousel
        item.originalParent.appendChild(item.player);
    });
    // Clear the array for the next time the modal is opened
    this.movedVideoPlayers = [];
}
  
  // Add this method to the ProductGallery class
  disconnectedCallback() {
      clearTimeout(this.galleryViewportTimer);
      this.spinInputCleanups?.forEach(cleanup => cleanup());
      this.lightboxSwiper?.destroy(true, true);
      if (this.galleryLightbox) {
        if (this.galleryLightbox.open) this.galleryLightbox.close();
        if (this.enlargedSpinId && window.Magic360) window.Magic360.stop(this.enlargedSpinId);
        this.galleryLightbox.remove();
      }
      if (this.galleryResizeHandler) window.removeEventListener('resize', this.galleryResizeHandler);
      cancelAnimationFrame(this.galleryLayoutFrame);
      if (this.modalObserver) {
          this.modalObserver.disconnect();
      }
  }

  connectedCallback() {
    const slideLimit = Number(this.mainCarouselWrapper.getAttribute('data-slide-limit'))
 
    this.mainCarousel.swiper.on('slideChange', (evt) => {
        const activeIndex = evt.activeIndex;
 
        if (activeIndex !== this.selectedIndex) {
            this.selectSlide(activeIndex)
        }
        function updateCountdownVisibility() {
            var countdownDisplay = document.querySelector('.product_gallery__main__countdown');
            var mobileTitle = document.querySelector('.product__main__title.mobile-only');
            if (countdownDisplay && mobileTitle) {
                var isMobile = getComputedStyle(mobileTitle).display === 'none';
                if (isMobile) {
                    countdownDisplay.style.display = (activeIndex === 0) ? 'block' : 'none';
                } else {
                    countdownDisplay.style.display = 'block';
                }
            }
        }
        updateCountdownVisibility();
        window.addEventListener('resize', updateCountdownVisibility);
        this.mainCarouselWrapper.setAttribute('data-current-slide', activeIndex)
        if (this.magic360Shortcut) this.handleMagic360Shortcut(activeIndex, slideLimit)
        if (activeIndex === slideLimit) {
            this.mainCarouselWrapper.classList.add('slider-end-reached')
        } else if (this.mainCarouselWrapper.classList.contains('slider-end-reached')) {
            this.mainCarouselWrapper.classList.remove('slider-end-reached')
        }
        this.pauseExitedVideo(evt);
    });
 
    this.nextSlideBtn.addEventListener('click', () => {
        if (this.galleryToolbar) return this.stepGallery(1);
        this.mainCarousel.swiper.slideNext()
    });
 
    this.prevSlideBtn.addEventListener('click', () => {
        if (this.galleryToolbar) return this.stepGallery(-1);
        this.mainCarousel.swiper.slidePrev()
    });

    /*
    // This is the debugging block for the zoom button click.
    this.slideZooms.forEach((zoom) => {
        zoom.addEventListener('click', (evt) => {
            console.clear();
            console.log("--- ZOOM BUTTON CLICKED ---");
            const slide = evt.currentTarget.closest('.swiper-slide');
            const customIndex = evt.currentTarget.getAttribute('data-index');
            console.log("The button's 'data-index' is:", customIndex);
            if (slide) {
                console.log("The carousel slide's internal index ('data-swiper-slide-index') is:", slide.dataset.swiperSlideIndex);
            }
            console.log("---------------------------");

            // Original function call
            this.selectZoomImage(customIndex); 
        });
    });
    */

    // Replace the existing slideZooms listener with this final version.
    this.slideZooms.forEach((zoom) => {
        zoom.addEventListener('click', (evt) => {
            // Get the index from the button that was clicked.
            let originalIndex = parseInt(evt.currentTarget.getAttribute('data-index'), 10);
    
            if (isNaN(originalIndex)) return;
    
            let correctedIndex = originalIndex; // Start with the original index.
    
            // --- START: Mobile Index Correction Logic ---
            
            const isMobileView = window.innerWidth < 990; // The mobile breakpoint
            const featuresSlide = document.getElementById('product-features-mobile-slide');
            
            // This logic runs only on mobile, AND only if the special features slide exists.
            if (isMobileView && featuresSlide) {
                
                // Get all the slides currently in the carousel to find the position of our special slide.
                const allSlidesInCarousel = Array.from(featuresSlide.parentElement.children);
                const featuresSlideIndex = allSlidesInCarousel.indexOf(featuresSlide);
    
                // This is the key: If the button we clicked belongs to a slide that comes *after*
                // the special mobile-only slide, we must subtract 1 from its index to match
                // the modal's list of items (which doesn't have the features slide).
                // We compare the button's index to the feature slide's actual position.
                if (featuresSlideIndex > -1 && originalIndex >= featuresSlideIndex) {
                    correctedIndex = originalIndex - 1;
                }
            }
            // --- END: Mobile Index Correction Logic ---
    
            // Use the (now corrected) index to show the right slide in the modal.
            this.selectZoomImage(correctedIndex);
        });
    });
 
    if (this.zoomModal) {
        this.modalObserver = new MutationObserver(this.handleModalToggle.bind(this));
        this.modalObserver.observe(this.zoomModal, { attributes: true, attributeFilter: ['open'] });
    }
 
    this.targetSlideBtn.forEach((btn) => btn.addEventListener('click', (evt) => {
        const targetIndex = Number(btn.getAttribute('data-slide-target'))
        this.mainCarousel.swiper.slideTo(targetIndex)
    }));
 
    this.initInlineVideo();
    this.initDragOrClick();
    this.initGalleryToolbar();
  }

  initGalleryToolbar() {
    if (this.dataset.galleryLayout !== 'toolbar') return;
    this.galleryToolbar = this.querySelector('[data-gallery-toolbar]');
    if (!this.galleryToolbar) return;
    this.initGallerySpinReadyHook();
    this.galleryLastIndex = this.mainCarousel.swiper.activeIndex;
    this.mainCarousel.swiper.slides.forEach(slide => {
      if (slide.dataset.slideId === 'spin') {
        slide.classList.add('swiper-no-swiping');
        this.bindGallerySpinInput(slide.querySelector('.Magic360'));
      }
    });
    this.galleryToolbar.querySelectorAll('[data-gallery-mode]').forEach(button => {
      button.addEventListener('click', () => this.setGalleryMode(button.dataset.galleryMode));
    });
    this.galleryToolbar.querySelectorAll('[data-gallery-step]').forEach(button => {
      button.addEventListener('click', () => this.stepGallery(Number(button.dataset.galleryStep)));
    });
    this.galleryResizeHandler = event => {
      this.syncGalleryToolbar();
      this.fitGallerySpin();
      if (!event.isTrusted) return;
      clearTimeout(this.galleryViewportTimer);
      this.galleryViewportTimer = setTimeout(() => {
        const swiper = this.mainCarousel.swiper;
        if (!this.isConnected || swiper.destroyed) return;
        const index = swiper.activeIndex;
        swiper.allowSlideNext = true;
        swiper.allowSlidePrev = true;
        swiper.update();
        swiper.slideTo(index, 0);
        this.syncGalleryToolbar();
        this.fitGallerySpin();
        this.lightboxSwiper?.update();
      }, 140);
    };
    window.addEventListener('resize', this.galleryResizeHandler);
    this.initGalleryLightbox();
    this.syncGalleryToolbar();
  }

  initGalleryLightbox() {
    this.galleryLightbox = this.querySelector('[data-gallery-lightbox]');
    this.galleryEnlarge = this.galleryToolbar.querySelector('[data-gallery-enlarge]');
    if (!this.galleryLightbox || !this.galleryEnlarge) return;
    document.body.append(this.galleryLightbox);
    this.galleryEnlarge.addEventListener('click', () => {
      this.galleryLightbox.showModal();
      this.galleryPreviousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.renderGalleryLightbox();
    });
    this.galleryLightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.galleryLightbox.close());
    this.galleryLightbox.addEventListener('close', () => {
      document.body.style.overflow = this.galleryPreviousOverflow || '';
      this.clearLightboxVideo();
      this.galleryEnlarge.focus({ preventScroll: true });
    });
    this.galleryLightbox.querySelectorAll('[data-lightbox-step]').forEach(button => {
      button.addEventListener('click', () => this.stepGalleryLightbox(Number(button.dataset.lightboxStep)));
    });
    this.galleryLightbox.querySelector('[data-lightbox-zoom]').addEventListener('click', () => this.toggleGalleryLightboxZoom());
    this.galleryLightbox.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        if (this.dataset.galleryMode === 'spin') {
          event.preventDefault();
          const viewer = document.getElementById(this.enlargedSpinId);
          if (viewer?.dataset.spinReady === 'true' && window.Magic360) {
            window.Magic360.spin(this.enlargedSpinId, event.key === 'ArrowLeft' ? -1 : 1);
            const status = viewer.querySelector('[data-spin-status]');
            if (status) status.hidden = true;
          }
          return;
        }
        if (this.galleryLightbox.dataset.zoomed === 'true') return;
        event.preventDefault();
        this.stepGalleryLightbox(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
  }

  clearLightboxVideo() {
    this.galleryLightbox.querySelectorAll('[data-lightbox-video]').forEach(host => {
      host.querySelector('video')?.pause();
      host.replaceChildren();
    });
  }

  initLightboxSwiper() {
    if (this.lightboxSwiper) return;
    this.lightboxIndices = this.galleryMediaIndices();
    const wrapper = this.galleryLightbox.querySelector('[data-lightbox-slides]');
    this.lightboxIndices.forEach(index => {
      const source = this.mainCarousel.swiper.slides[index];
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.dataset.galleryIndex = index;
      if (source.classList.contains('swiper-slide--video')) {
        const host = document.createElement('div');
        host.className = 'product-gallery-lightbox__video swiper-no-swiping';
        host.dataset.lightboxVideo = '';
        slide.append(host);
      } else {
        const host = document.createElement('div');
        host.className = 'swiper-zoom-container';
        host.dataset.lightboxImage = '';
        const image = source.querySelector('img');
        if (image) {
          const photo = document.createElement('img');
          const url = new URL(image.currentSrc || image.src, window.location.href);
          url.searchParams.set('width', '2048');
          photo.dataset.lightboxSource = url.href;
          photo.alt = image.alt;
          photo.width = image.naturalWidth || image.width || 2048;
          photo.height = image.naturalHeight || image.height || 2048;
          photo.decoding = 'async';
          photo.draggable = false;
          host.append(photo);
        }
        slide.append(host);
      }
      wrapper.append(slide);
    });
    const SwiperClass = this.mainCarousel.swiper.constructor;
    this.lightboxSwiper = new SwiperClass(this.galleryLightbox.querySelector('[data-lightbox-carousel]'), {
      init: false,
      slidesPerView: 1,
      speed: 300,
      loop: false,
      rewind: true,
      grabCursor: true,
      zoom: { maxRatio: 3, minRatio: 1 },
      on: {
        slideChange: () => this.syncLightboxSlide(),
        zoomChange: (swiper, scale) => {
          const zoomed = scale > 1;
          this.galleryLightbox.dataset.zoomed = String(zoomed);
          this.galleryLightbox.querySelector('[data-lightbox-zoom]').setAttribute('aria-pressed', String(zoomed));
        }
      }
    });
    this.lightboxSwiper.init();
  }

  syncLightboxSlide() {
    const swiper = this.lightboxSwiper;
    if (!swiper || this.galleryLightbox.dataset.mode !== 'gallery') return;
    this.clearLightboxVideo();
    const position = swiper.activeIndex;
    const index = this.lightboxIndices[position];
    if (index === undefined) return;
    this.mainCarousel.swiper.allowSlideNext = true;
    this.mainCarousel.swiper.allowSlidePrev = true;
    this.mainCarousel.swiper.slideTo(index, 0);
    this.syncGalleryToolbar();
    this.galleryLightbox.dataset.zoomed = 'false';
    const zoom = this.galleryLightbox.querySelector('[data-lightbox-zoom]');
    zoom.setAttribute('aria-pressed', 'false');
    zoom.hidden = !!swiper.slides[position]?.querySelector('[data-lightbox-video]');
    this.galleryLightbox.querySelector('[data-lightbox-count]').textContent = `${position + 1} / ${this.lightboxIndices.length}`;
    [position - 1, position, position + 1].forEach(offset => {
      const adjacent = (offset + this.lightboxIndices.length) % this.lightboxIndices.length;
      const photo = swiper.slides[adjacent]?.querySelector('[data-lightbox-source]');
      if (photo && !photo.getAttribute('src')) photo.src = photo.dataset.lightboxSource;
    });
    const host = swiper.slides[position]?.querySelector('[data-lightbox-video]');
    const source = this.mainCarousel.swiper.slides[index];
    if (host) {
      const player = source.querySelector('[js-video-wrapper] video, [js-video-wrapper] iframe');
      if (player) {
        this.pauseExitedVideo({ slides: [source], previousIndex: 0 });
        const clone = player.cloneNode(true);
        clone.removeAttribute('autoplay');
        host.append(clone);
      }
    }
  }

  toggleGalleryLightboxZoom() {
    this.lightboxSwiper?.zoom.toggle();
  }

  stepGalleryLightbox(step) {
    if (this.dataset.galleryMode === 'spin' || !this.lightboxSwiper) return;
    this.lightboxSwiper.zoom.out();
    if (step > 0) this.lightboxSwiper.slideNext();
    else this.lightboxSwiper.slidePrev();
  }

  renderGalleryLightbox() {
    const dialog = this.galleryLightbox;
    const slide = this.mainCarousel.swiper.slides[this.mainCarousel.swiper.activeIndex];
    const isSpin = slide?.dataset.slideId === 'spin';
    dialog.dataset.zoomed = 'false';
    dialog.dataset.mode = isSpin ? 'spin' : 'gallery';
    const carousel = dialog.querySelector('[data-lightbox-carousel]');
    const spinHost = dialog.querySelector('[data-lightbox-spin]');
    carousel.hidden = isSpin;
    spinHost.hidden = !isSpin;
    this.clearLightboxVideo();
    const zoom = dialog.querySelector('[data-lightbox-zoom]');
    zoom.hidden = isSpin;
    zoom.setAttribute('aria-pressed', 'false');
    dialog.querySelector('[data-lightbox-navigation]').hidden = isSpin;
    dialog.querySelector('[data-lightbox-title]').textContent = isSpin ? dialog.dataset.spinTitle : dialog.dataset.galleryTitle;
    if (isSpin) {
      if (!this.enlargedSpinId) {
        const viewer = slide.querySelector('.Magic360');
        const options = viewer?.getAttribute('data-magic360-options') || '';
        const frames = options.match(/(?:^|;)\s*large-images:\s*([^;]+)/)?.[1]?.trim();
        if (!frames || !window.Magic360) return;
        this.enlargedSpinId = `${dialog.id}-spin`;
        const spin = document.createElement('div');
        spin.id = this.enlargedSpinId;
        spin.className = 'Magic360';
        const columns = options.match(/(?:^|;)\s*columns:\s*(\d+)/)?.[1] || frames.split(/\s+/).length;
        const side = Math.min(window.innerWidth, Math.max(1, window.innerHeight - 72));
        const frameWidth = Math.min(1280, Math.max(600, Math.ceil(side * Math.min(window.devicePixelRatio || 1, 2))));
        const displayFrames = frames.split(/\s+/).map(source => {
          const url = new URL(source, window.location.href);
          url.searchParams.set('width', String(frameWidth));
          return url.href;
        }).join(' ');
        spin.setAttribute('data-magic360-options', `columns: ${columns}; images: ${displayFrames}; large-images: ${frames}; autospin: off; smoothing: false; reverse-column: true; magnify: false; fullscreen: false; initialize-on: load; hint: true;`);
        const first = document.createElement('img');
        first.src = displayFrames.split(/\s+/)[0];
        first.alt = viewer.querySelector('img')?.alt || '';
        spin.append(first);
        spinHost.append(spin);
        window.Magic360.start(this.enlargedSpinId);
        this.bindGallerySpinInput(spin);
      }
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    } else {
      const index = this.mainCarousel.swiper.activeIndex;
      this.initLightboxSwiper();
      this.lightboxSwiper.zoom.out();
      this.lightboxSwiper.update();
      this.lightboxSwiper.slideTo(this.lightboxIndices.indexOf(index), 0, false);
      this.syncLightboxSlide();
    }
  }

  initGallerySpinReadyHook() {
    if (!window.Magic360 || ProductGallery.spinReadyHookInstalled) return;
    const previous = window.Magic360.options.onready;
    window.Magic360.options.onready = function(spin) {
      const id = typeof spin === 'string' ? spin : spin?.id;
      const viewer = document.getElementById(id);
      if (viewer) {
        viewer.dataset.spinReady = 'true';
        viewer.dispatchEvent(new Event('gallery-spin-ready'));
      }
      if (typeof previous === 'function') return previous.apply(this, arguments);
    };
    const previousSpin = window.Magic360.options.onspin;
    window.Magic360.options.onspin = function(spin) {
      const id = typeof spin === 'string' ? spin : spin?.id;
      const viewer = document.getElementById(id);
      const status = viewer?.querySelector('[data-spin-status]');
      if (status?.dataset.state === 'ready' && viewer.dataset.spinInteracted === 'true') status.hidden = true;
      if (typeof previousSpin === 'function') return previousSpin.apply(this, arguments);
    };
    ProductGallery.spinReadyHookInstalled = true;
  }

  bindGallerySpinStatus(viewer) {
    const existing = viewer.querySelector('[data-spin-status]');
    if (existing) return existing;
    const status = document.createElement('div');
    status.className = 'product-gallery-spin-status';
    status.dataset.spinStatus = '';
    status.dataset.state = 'loading';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    const loading = document.createElement('span');
    loading.dataset.spinLoading = '';
    const drag = document.createElement('span');
    drag.dataset.spinDrag = '';
    drag.textContent = this.galleryToolbar.dataset.spinDrag;
    const swipe = document.createElement('span');
    swipe.dataset.spinSwipe = '';
    swipe.textContent = this.galleryToolbar.dataset.spinSwipe;
    const message = document.createElement('span');
    message.className = 'm360-hint-message';
    message.append(loading, drag, swipe);
    status.append(message);
    viewer.append(status);
    let lastProgress = null;
    const ready = () => {
      if (status.dataset.state === 'ready') return;
      viewer.dataset.spinReady = 'true';
      viewer.setAttribute('aria-busy', 'false');
      status.dataset.state = 'ready';
      observer.disconnect();
    };
    const update = () => {
      const loader = viewer.querySelector('.m360-loader[data-progress]');
      const progress = loader ? parseInt(loader.getAttribute('data-progress'), 10) : NaN;
      if (viewer.dataset.spinReady === 'true' || (viewer.querySelector('canvas') && progress >= 100)) {
        ready();
        return;
      }
      const text = this.galleryToolbar.dataset.spinLoading + (Number.isFinite(progress) ? ` ${Math.min(99, Math.max(0, progress))}%` : '');
      if (lastProgress !== text) {
        loading.textContent = text;
        lastProgress = text;
      }
    };
    const observer = new MutationObserver(update);
    observer.observe(viewer, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-progress'] });
    viewer.setAttribute('aria-busy', 'true');
    viewer.addEventListener('gallery-spin-ready', ready);
    update();
    (this.spinInputCleanups ||= []).push(() => {
      observer.disconnect();
      viewer.removeEventListener('gallery-spin-ready', ready);
      status.remove();
    });
    return status;
  }

  bindGallerySpinInput(viewer) {
    if (!viewer || !viewer.id) return;
    const status = this.bindGallerySpinStatus(viewer);
    if (ProductGallery.nativeTouchSpin) {
      if (viewer.dataset.spinInputMode === 'native') return;
      viewer.dataset.spinInputMode = 'native';
      const interact = () => {
        if (viewer.dataset.spinReady === 'true') viewer.dataset.spinInteracted = 'true';
      };
      viewer.addEventListener('pointerdown', interact, { passive: true });
      viewer.addEventListener('touchstart', interact, { passive: true });
      this.spinInputCleanups.push(() => {
        viewer.removeEventListener('pointerdown', interact);
        viewer.removeEventListener('touchstart', interact);
        delete viewer.dataset.spinInputMode;
        delete viewer.dataset.spinInteracted;
      });
      return;
    }
    if (!window.PointerEvent || viewer.querySelector('[data-spin-input]')) return;
    const surface = document.createElement('div');
    surface.className = 'product-gallery-spin-input';
    surface.dataset.spinInput = '';
    surface.setAttribute('aria-hidden', 'true');
    viewer.append(surface);
    const columns = Number(viewer.getAttribute('data-magic360-options')?.match(/(?:^|;)\s*columns:\s*(\d+)/)?.[1]) || 36;
    let drag = null;
    const listen = (name, callback, options) => {
      surface.addEventListener(name, callback, options);
      (this.spinInputCleanups ||= []).push(() => surface.removeEventListener(name, callback, options));
    };
    const finish = () => {
      if (drag && surface.hasPointerCapture?.(drag.id)) surface.releasePointerCapture(drag.id);
      drag = null;
      surface.removeAttribute('data-dragging');
    };
    listen('pointerdown', event => {
      event.stopPropagation();
      if (viewer.dataset.spinReady !== 'true' || !event.isPrimary || event.button !== 0) {
        finish();
        return;
      }
      if (event.pointerType === 'mouse') event.preventDefault();
      finish();
      window.Magic360?.pause?.(viewer.id);
      drag = { id: event.pointerId, x: event.clientX, remainder: 0, step: Math.max(3, surface.clientWidth / columns) };
      surface.setPointerCapture?.(event.pointerId);
      surface.setAttribute('data-dragging', '');
    });
    listen('pointermove', event => {
      event.stopPropagation();
      if (!drag || drag.id !== event.pointerId) return;
      drag.remainder += drag.x - event.clientX;
      drag.x = event.clientX;
      const frames = Math.trunc(drag.remainder / drag.step);
      if (frames) {
        drag.remainder -= frames * drag.step;
        window.Magic360?.spin(viewer.id, frames);
        status.hidden = true;
        const hint = viewer.querySelector('.m360-hint');
        if (hint) hint.style.opacity = '0';
      }
    });
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      listen(name, event => {
        event.stopPropagation();
        if (drag?.id === event.pointerId) finish();
      });
    }
    for (const name of ['mousedown', 'touchstart', 'touchmove', 'touchend', 'click', 'dragstart']) {
      listen(name, event => event.stopPropagation(), { passive: true });
    }
    this.spinInputCleanups.push(() => { finish(); surface.remove(); });
  }

  async measureGallerySpin() {
    if (this.spinMeasurementStarted) return;
    this.spinMeasurementStarted = true;
    const slide = Array.from(this.mainCarousel.swiper.slides).find(item => item.dataset.slideId === 'spin');
    const options = slide?.querySelector('.Magic360')?.getAttribute('data-magic360-options') || '';
    const sources = options.match(/(?:^|;)\s*images:\s*([^;]+)/)?.[1]?.trim().split(/\s+/);
    if (!sources?.length) return;
    const bounds = { left: 1, top: 1, right: 0, bottom: 0 };
    try {
      for (let offset = 0; offset < sources.length; offset += 4) {
        const frames = await Promise.all(sources.slice(offset, offset + 4).map(source => new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = 96;
              canvas.height = 96;
              const context = canvas.getContext('2d', { willReadFrequently: true });
              context.drawImage(image, 0, 0, 96, 96);
              resolve(this.spinImageBounds(context.getImageData(0, 0, 96, 96).data, 96));
            } catch (error) { reject(error); }
          };
          image.onerror = reject;
          const url = new URL(source, window.location.href);
          url.searchParams.set('width', '96');
          image.src = url.href;
        })));
        for (const frame of frames) {
          if (!frame) return;
          bounds.left = Math.min(bounds.left, frame.left);
          bounds.top = Math.min(bounds.top, frame.top);
          bounds.right = Math.max(bounds.right, frame.right);
          bounds.bottom = Math.max(bounds.bottom, frame.bottom);
        }
        if (!this.isConnected) return;
      }
      this.spinBounds = bounds;
      this.fitGallerySpin();
    } catch (error) {
      this.spinBounds = null;
    }
  }

  spinImageBounds(pixels, size) {
    let left = size, top = size, right = -1, bottom = -1;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const offset = (y * size + x) * 4;
        if (pixels[offset + 3] > 32 && Math.min(pixels[offset], pixels[offset + 1], pixels[offset + 2]) < 242) {
          left = Math.min(left, x);
          right = Math.max(right, x);
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
        }
      }
    }
    if (right <= left || bottom <= top) return null;
    const padding = 5;
    return { left: Math.max(0, (left - padding) / size), top: Math.max(0, (top - padding) / size), right: Math.min(1, (right + padding + 1) / size), bottom: Math.min(1, (bottom + padding + 1) / size) };
  }

  fitGallerySpin() {
    if (!this.spinBounds || this.dataset.galleryMode !== 'spin') return;
    const slide = Array.from(this.mainCarousel.swiper.slides).find(item => item.dataset.slideId === 'spin');
    if (!slide) return;
    const bounds = this.spinBounds;
    const size = Math.min(slide.clientWidth / (bounds.right - bounds.left), slide.clientHeight / (bounds.bottom - bounds.top));
    if (!Number.isFinite(size) || size <= 0) return;
    if (slide.style.getPropertyValue('--spin-size') === `${size}px`) return;
    slide.style.setProperty('--spin-size', `${size}px`);
    slide.style.setProperty('--spin-x', `${size * (0.5 - (bounds.left + bounds.right) / 2)}px`);
    slide.style.setProperty('--spin-y', `${size * (0.5 - (bounds.top + bounds.bottom) / 2)}px`);
    window.dispatchEvent(new Event('resize'));
  }

  galleryMediaIndices() {
    return Array.from(this.mainCarousel.swiper.slides).reduce((indices, slide, index) => {
      if (slide.dataset.slideId !== 'spin' && !slide.classList.contains('swiper-slide--html') && getComputedStyle(slide).display !== 'none') indices.push(index);
      return indices;
    }, []);
  }

  setGalleryMode(mode) {
    const swiper = this.mainCarousel.swiper;
    const spinIndex = Array.from(swiper.slides).findIndex(slide => slide.dataset.slideId === 'spin');
    if (mode === 'spin' && spinIndex < 0) return;
    const lastSlide = swiper.slides[this.galleryLastIndex];
    const galleryIndex = lastSlide && getComputedStyle(lastSlide).display !== 'none' ? this.galleryLastIndex : this.galleryMediaIndices()[0];
    const target = mode === 'spin' ? spinIndex : galleryIndex;
    if (target === undefined) return;
    swiper.allowSlideNext = true;
    swiper.allowSlidePrev = true;
    swiper.slideTo(target, 0);
    this.syncGalleryToolbar();
  }

  stepGallery(step) {
    const swiper = this.mainCarousel.swiper;
    if (swiper.slides[swiper.activeIndex]?.dataset.slideId === 'spin') return;
    const indices = Array.from(swiper.slides).reduce((visible, slide, index) => {
      if (slide.dataset.slideId !== 'spin' && getComputedStyle(slide).display !== 'none') visible.push(index);
      return visible;
    }, []);
    if (!indices.length) return;
    const current = indices.indexOf(swiper.activeIndex);
    const target = indices[(current + step + indices.length) % indices.length];
    swiper.allowSlideNext = true;
    swiper.allowSlidePrev = true;
    swiper.slideTo(target);
    this.syncGalleryToolbar();
  }

  syncGalleryToolbar() {
    if (!this.galleryToolbar) return;
    const swiper = this.mainCarousel.swiper;
    const activeSlide = swiper.slides[swiper.activeIndex];
    const isSpin = activeSlide?.dataset.slideId === 'spin';
    const indices = this.galleryMediaIndices();
    const visibleSlides = Array.from(swiper.slides).filter(slide => slide.dataset.slideId !== 'spin' && getComputedStyle(slide).display !== 'none');
    const lastGallerySlide = visibleSlides[visibleSlides.length - 1];
    if (!isSpin) this.galleryLastIndex = swiper.activeIndex;
    const mode = isSpin ? 'spin' : 'gallery';
    const modeChanged = this.dataset.galleryMode !== mode;
    this.dataset.galleryMode = mode;
    if (isSpin) this.measureGallerySpin();
    if (modeChanged) {
      cancelAnimationFrame(this.galleryLayoutFrame);
      this.galleryLayoutFrame = requestAnimationFrame(() => {
        if (!this.isConnected || swiper.destroyed) return;
        const target = swiper.activeIndex;
        swiper.allowSlideNext = true;
        swiper.allowSlidePrev = true;
        swiper.update();
        swiper.slideTo(target, 0);
        this.syncGalleryToolbar();
        this.fitGallerySpin();
        window.dispatchEvent(new Event('resize'));
      });
    }
    this.galleryToolbar.querySelectorAll('[data-gallery-mode]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.galleryMode === (isSpin ? 'spin' : 'gallery')));
    });
    const counter = this.galleryToolbar.querySelector('[data-gallery-count]');
    const current = indices.indexOf(swiper.activeIndex) + 1;
    if (this.galleryEnlarge) this.galleryEnlarge.disabled = activeSlide?.classList.contains('swiper-slide--html');
    if (isSpin) {
      counter.textContent = this.galleryToolbar.querySelector('[data-gallery-mode="spin"]').textContent.trim();
      counter.removeAttribute('aria-label');
    } else if (activeSlide?.classList.contains('swiper-slide--html')) {
      counter.textContent = this.querySelector('.ss-html [js-gallery-thumbnail-slide]')?.textContent.trim() || '';
      counter.removeAttribute('aria-label');
    } else {
      counter.textContent = `${String(current).padStart(2, '0')} / ${String(indices.length).padStart(2, '0')}`;
      counter.setAttribute('aria-label', counter.dataset.countLabel.replace('[current]', current).replace('[total]', indices.length));
    }
    this.galleryToolbar.querySelectorAll('[data-gallery-step]').forEach(button => { button.disabled = isSpin || visibleSlides.length < 2; });
    this.prevSlideBtn.disabled = isSpin || visibleSlides.length < 2;
    this.nextSlideBtn.disabled = isSpin || visibleSlides.length < 2;
    swiper.allowSlideNext = !isSpin && activeSlide !== lastGallerySlide;
    swiper.allowSlidePrev = !isSpin;
    swiper.allowTouchMove = !isSpin;
  }

  handleMagic360Shortcut(activeIndex, slideLimit) {
    if (activeIndex === slideLimit) {
      this.magic360Shortcut.innerHTML = 'View Image Gallery'
      this.magic360Shortcut.setAttribute('data-slide-target', 0)
    } else {
      this.magic360Shortcut.innerHTML = 'View 360°'
      this.magic360Shortcut.setAttribute('data-slide-target', slideLimit)
    }
  }

  selectZoomImage(index) {
    const numericIndex = parseInt(index, 10);

    // Safety check to ensure the index is valid and the element exists.
    if (isNaN(numericIndex) || !this.zoomImages[numericIndex]) {
        console.error('selectZoomImage called with an invalid or out-of-bounds index:', index);
        return;
    }

    const selectedImage = this.zoomImages[numericIndex];
    const modalGalleryContentInfo = this.zoomModal.querySelector('.modal-gallery__content-info');

    if (!modalGalleryContentInfo) {
        console.error('Could not find the modal scrolling container.');
        return;
    }

    // --- The Fix ---
    // The modal gallery always scrolls vertically. The previous code was incorrectly
    // trying to scroll horizontally (`scrollLeft`) on mobile.
    // This corrected code ALWAYS sets the vertical scroll position (`scrollTop`)
    // to the target element's vertical position (`offsetTop`).
    modalGalleryContentInfo.scrollTop = selectedImage.offsetTop;
  }

  selectSlide(selectedIndex, setMainCarousel = false) {
    this.currentSlide = selectedIndex

    this.thumbnails.forEach((thumbnail) => {
      if (thumbnail !== this.thumbnails[selectedIndex] && thumbnail.classList.contains('selected')) {
        thumbnail.classList.remove('selected')
      } else if (thumbnail === this.thumbnails[selectedIndex] && !thumbnail.classList.contains('selected')) {
        thumbnail.classList.add('selected')
      }
    })

    if (setMainCarousel) {
      if (this.galleryToolbar) {
        this.mainCarousel.swiper.allowSlideNext = true;
        this.mainCarousel.swiper.allowSlidePrev = true;
      }
      this.mainCarousel.swiper.slideTo(selectedIndex)
    }

    const selectedSlide = this.thumbnails[selectedIndex]

    if (selectedSlide?.getAttribute('data-magic360') === 'true') {
      this.scrollToLastThumbnail()
    } else {
      this.scrollToActiveThumbnail()
    }

    this.checkNavState(selectedIndex)
    this.syncGalleryToolbar()
  }

  checkNavState(selectedIndex) {
    if (selectedIndex == this.thumbnails.length - 1) {
      this.nextSlideBtn.setAttribute('disabled', true)
    } else if (this.nextSlideBtn.getAttribute('disabled') == 'true') {
      this.nextSlideBtn.removeAttribute('disabled')
    }

    if (selectedIndex >= 1 && this.prevSlideBtn.getAttribute('disabled') == 'true') {
      this.prevSlideBtn.removeAttribute('disabled')
    } else if (selectedIndex == 0) {
      this.prevSlideBtn.setAttribute('disabled', true)
    }
  }

  observeActiveThumbnail() {
    // Observer setup to auto-scroll to the active thumbnail
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          this.scrollToActiveThumbnail()
        }
      })
    })

    this.thumbnails.forEach((thumbnail) => {
      observer.observe(thumbnail, {
        attributes: true, // Listen for attribute changes
        attributeFilter: ['class'], // Specifically for class changes
      })
    })
  }

  scrollToLastThumbnail() {
    this.thumbnailsContainer.scrollTo({
      [this.thumbnailDirection.direction]: this.thumbnailsContainer[this.thumbnailDirection.scrollDimensions],
      behavior: 'smooth',
    });
  }

  scrollToActiveThumbnail() {
    const activeThumbnail = this.querySelector(`${this._selectors.thumbnail}.selected`)

    if (activeThumbnail) {
      this.thumbnailsContainer.scrollTo({
        [this.thumbnailDirection.direction]: activeThumbnail[this.thumbnailDirection.offset] - this.thumbnailsContainer[this.thumbnailDirection.offset],
        behavior: 'smooth',
      })
    }
  }

  initDragOrClick() {

    this.thumbnailsContainer.addEventListener('mousedown', (e) => {
      e.preventDefault(); // Prevent default action to avoid dragging images or selecting text
      this.isDragging = false; // Assume it's not dragging initially
      this.hasMoved = false; // Reset moved state
      this.startPos = e[this.thumbnailDirection.page];
      this.scrollStartPos = this.thumbnailsContainer[this.thumbnailDirection.scrollAxis];
      document.addEventListener('mousemove', this.boundHandleMouseMove);
    });

    document.addEventListener('mouseup', (e) => {
      document.removeEventListener('mousemove', this.boundHandleMouseMove);
      this.isDragging && e.preventDefault(); // Prevent click event if it was a drag
      this.isDragging = false;
    });

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (e) => {
        !this.hasMoved ? this.handleClick(thumbnail) : e.stopImmediatePropagation();
      });
    });

  }

  handleMouseMove(e) {
    if (Math.abs(e[this.thumbnailDirection.page] - this.startPos) > 5 && !this.isDragging) {
      this.isDragging = true;
      this.thumbnailsContainer.style.userSelect = 'none';
      this.thumbnailsContainer.style.cursor = 'grabbing';
    }
    if (this.isDragging) {
      this.hasMoved = true; // Flag as moved for click handler check
      this.thumbnailsContainer[this.thumbnailDirection.scrollAxis] = this.scrollStartPos - (e[this.thumbnailDirection.page] - this.startPos);
    }
  }

  handleClick(thumbnail) {
    const selectedIndex = Number(thumbnail.getAttribute('data-index'));
    this.selectSlide(selectedIndex, true);
  }

  initInlineVideo() {
      this.querySelectorAll(this._selectors.inlinePlayButton).forEach(button => {
          button.addEventListener('click', this.playInlineVideo.bind(this));
      });
  }
  
  playInlineVideo(event) {
      event.preventDefault();
      const playButton = event.currentTarget;
      const slide = playButton.closest(this._selectors.videoSlide);
      if (!slide) return;
  
      const poster = slide.querySelector(this._selectors.videoPoster);
      const videoWrapper = slide.querySelector(this._selectors.videoWrapper);
  
      // If the video wrapper doesn't exist, stop here to prevent an error.
      if (!videoWrapper) {
          console.error('Product Gallery: Video wrapper not found in this slide.', slide);
          return;
      }
  
      const iframe = videoWrapper.querySelector('iframe');
      const video = videoWrapper.querySelector('video');
  
      if (poster) poster.classList.add('video-poster--hidden');
      videoWrapper.hidden = false;
  
      // Autoplay embedded videos
      if (iframe && !iframe.src.includes('autoplay=1')) {
          iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'autoplay=1';
      } else if (video) {
          video.play();
      }
  }
  
  pauseExitedVideo(swiperEvent) {
      const previousSlide = swiperEvent.slides[swiperEvent.previousIndex];
      
      if (previousSlide && previousSlide.classList.contains('swiper-slide--video')) {
          const poster = previousSlide.querySelector(this._selectors.videoPoster);
          const videoWrapper = previousSlide.querySelector(this._selectors.videoWrapper);
  
          // If video isn't playing, do nothing
          if (!videoWrapper || videoWrapper.hidden) return; 
          
          const iframe = videoWrapper.querySelector('iframe');
          const video = videoWrapper.querySelector('video');
  
          // Stop/pause the video and reset the slide to its initial state
          if (iframe) {
              // Re-setting the src is the most reliable way to stop an iframe video
              iframe.src = iframe.src.replace('&autoplay=1', '').replace('?autoplay=1', '');
          } else if (video) {
              video.pause();
          }
  
          videoWrapper.hidden = true;
          if (poster) poster.classList.remove('video-poster--hidden');
      }
  }

}

if (!customElements.get('product-gallery')) {
  customElements.define("product-gallery", ProductGallery);
}
